/**
 * NeedsSummary — per-domain identified-needs read for the navigator, plus the
 * SafetyResources banner shown whenever interpersonal safety is positive.
 *
 * The screener is a decision-support aid: it flags likely unmet social needs by
 * domain so a CHW/Promotor can connect the member to resources. It is not a
 * diagnosis and does not auto-refer.
 */

import React from 'react';
import { AlertTriangle, CheckCircle2, ShieldAlert, Phone, ArrowRightCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { type CreatedReferral, type DomainResult, SAFETY_RESOURCES } from './types';

/** Safety-net resources — always shown when an interpersonal-safety need triggers. */
export const SafetyResources: React.FC<{ title?: string }> = ({
  title = 'Support resources',
}) => (
  <Card className="border-red-300 bg-red-50">
    <CardHeader className="pb-2">
      <CardTitle className="flex items-center gap-2 text-red-800">
        <ShieldAlert className="h-5 w-5" aria-hidden="true" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2">
      <p className="text-sm text-red-800">
        If you or someone you know may be unsafe, these free, confidential resources can help.
      </p>
      <ul className="space-y-2">
        {SAFETY_RESOURCES.map((r) => (
          <li key={r.name} className="rounded-md border border-red-200 bg-white p-3">
            <div className="font-medium text-foreground">{r.name}</div>
            <div className="text-sm text-muted-foreground">{r.detail}</div>
            <a
              href={r.href}
              className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-red-700 hover:underline"
            >
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              {r.action}
            </a>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

/**
 * Referrals auto-created by the clinical<->social handoff from the positive
 * domains. Shown to the navigator so the screen->service step is visible and
 * not a manual to-do. Only rendered when the handoff is enabled for the org.
 */
const CreatedReferrals: React.FC<{ referrals: CreatedReferral[] }> = ({ referrals }) => {
  const created = referrals.filter((r) => r.created);
  if (created.length === 0) return null;
  return (
    <Card className="border-emerald-300 bg-emerald-50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-emerald-800">
          <ArrowRightCircle className="h-5 w-5" aria-hidden="true" />
          Referrals created
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-emerald-800">
          A service referral was opened automatically for each identified need. Track them in the
          referrals workspace.
        </p>
        <ul className="space-y-2">
          {created.map((r) => (
            <li
              key={r.referral_id ?? r.domain}
              className="flex items-center justify-between rounded-md border border-emerald-200 bg-white p-3"
            >
              <div>
                <div className="font-medium text-foreground">{r.service_type ?? r.domain}</div>
                <div className="text-xs text-muted-foreground">Need: {r.domain}</div>
              </div>
              {r.referral_number && (
                <Badge variant="secondary" className="font-mono text-[11px]">
                  {r.referral_number}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

interface NeedsSummaryProps {
  domains: DomainResult[];
  isSimulation?: boolean;
  referrals?: CreatedReferral[];
}

export const NeedsSummary: React.FC<NeedsSummaryProps> = ({ domains, isSimulation, referrals }) => {
  const positives = domains.filter((d) => d.positive);
  const safetyPositive = domains.some((d) => d.positive && d.is_safety_critical);

  return (
    <div className="space-y-4">
      {safetyPositive && <SafetyResources />}
      {referrals && referrals.length > 0 && <CreatedReferrals referrals={referrals} />}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Identified needs</span>
            <Badge variant={positives.length > 0 ? 'destructive' : 'secondary'}>
              {positives.length} of {domains.length} domains
            </Badge>
          </CardTitle>
          {isSimulation && (
            <p className="text-xs font-medium text-amber-600">
              Simulated result — synthetic data, no PHI.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {domains.length === 0 && (
            <p className="text-sm text-muted-foreground">No results yet.</p>
          )}
          {domains.map((d) => (
            <div
              key={d.domain_code}
              className={`flex items-start gap-3 rounded-md border p-3 ${
                d.positive ? 'border-amber-300 bg-amber-50' : 'border-muted'
              }`}
            >
              {d.positive ? (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 font-medium">
                  {d.domain_name ?? d.domain_code}
                  {d.is_safety_critical && d.positive && (
                    <Badge variant="destructive" className="text-[10px]">
                      Safety
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">{d.detail}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
